import { FeatureToggleDto } from "@gap/backend/app-services/feature-toggle/models/FeatureToggleDto"
import { notNull } from "@gap/utils/utils"
import { apiClient } from "api/trpc/trpc-client"
import { makeAutoObservable } from "mobx"
import Loader from "reusable/mobx-helpers/Loader"
import ModalState from "reusable/mobx-helpers/ModalState"
import { formHandler, FormHandlerConfig } from "reusable/mobx-store-utils/form-handlers/new/form-handler"
import { createStoreContext } from "reusable/mobx-store-utils/storeUtils"
import { orgTypeEnums } from '@gap/backend/app-services/org/models/OrgTypeEnum'
import { enumToAutoCompleteOption } from "features/gap-components/autocomplete/single/GapAutocompleteSingle"
import { regionPathMap } from "@gap/utils/region-utils"
import { OrgTypeEnum } from "@gap/backend/app-services/org/models/OrgTypeEnum"
import { GapAutocompleteHierarchy } from "features/gap-components/autocomplete/types/GapAutocompleteTypes"
import { setLoadingOnRow } from "reusable/mui/data-grid/GapDataGridSkeletonCell"

export type FeatureToggleTabId = 'toggles' | 'beta-opt-ins'

export default class FeatureToggleStore {
   featureToggles: FeatureToggleDto.entity[] = []
   setupModal = new ModalState()
   selectedFeatureToggle: FeatureToggleDto.entity | undefined
   orgs: { id: string, name: string, orgType: OrgTypeEnum }[] = []
   betaOptedInOrgs: { id: string, name: string }[] = []
   activeTab: FeatureToggleTabId = 'toggles'
   loader = new Loader(true)
   saveLoader = new Loader(false)

   constructor() {
      makeAutoObservable(this)
   }

   get betaToggleCount() {
      return this.featureToggles.filter(t => t.releasedToBeta).length
   }

   get formHandler() {
      if (!this.selectedFeatureToggle) {
         return null
      }
      return formHandler(this.selectedFeatureToggle, {
         enabledForAllOrgs: { type: 'boolean' },
         releasedToBeta: { type: 'boolean' },
         onlyForOrgTypes: { type: 'array' },
         onlyForOrgIds: { type: 'array' },
         onlyForRegion: { type: 'string' },
         onlyForUserIds: { type: 'array' },
         onlyForOrgsCreatedAfter: { type: 'string' },
      } satisfies FormHandlerConfig)
   }

   get regionOptions() {
      return Object.keys(regionPathMap).map(region => ({ label: region, id: region }))
   }

   get orgTypeOptions() {
      return enumToAutoCompleteOption(orgTypeEnums)
   }

   get orgIdOptions(): GapAutocompleteHierarchy {
      const categoryDefs: { id: OrgTypeEnum, label: string }[] = [
         { id: 'gap', label: 'Gap' },
         { id: 'advisor', label: 'Advisors' },
         { id: 'client', label: 'Clients' },
      ]
      return {
         categories: categoryDefs
            .map(({ id, label }) => ({
               id,
               label,
               items: this.orgs
                  .filter(org => org.orgType === id)
                  .map(org => ({ label: `${org.name} (${org.id})`, id: org.id })),
            }))
            .filter(category => category.items.length > 0),
      }
   }

   changeTab = (_: React.SyntheticEvent, tab: FeatureToggleTabId) => {
      this.activeTab = tab
   }

   toggleEnable = async (event: React.MouseEvent<HTMLElement>) => {
      await this.loader.executeLoading(async () => {
         const id = notNull(event.currentTarget.getAttribute("data-id"))
         const featureToggle = notNull(this.featureToggles.find(featureToggle => featureToggle.id === id))
         featureToggle.enabled = !featureToggle.enabled
         await apiClient.featureToggle.update.mutate(featureToggle)
      })
   }

   fetch = async () => {
      await this.loader.executeLoading(async () => {
         const [featureToggles, orgs, betaOptedInOrgs] = await Promise.all([
            apiClient.featureToggle.getAll.query(),
            apiClient.org.listNames.query(),
            apiClient.featureToggle.getBetaOptedInOrgs.query(),
         ])
         this.orgs = orgs
         this.featureToggles = featureToggles
         this.betaOptedInOrgs = betaOptedInOrgs
      })
   }

   openSetupModal = (event: React.MouseEvent<HTMLElement>) => {
      const id = notNull(event.currentTarget.getAttribute("data-id"))
      this.selectedFeatureToggle = notNull(this.featureToggles.find(featureToggle => featureToggle.id === id))
      this.setupModal.open()
   }

   onUserIdChange = (_: any, newValue: string[]) => {
      if (this.selectedFeatureToggle) {
         this.selectedFeatureToggle.onlyForUserIds = newValue
      }
   }

   save = async () => {
      await this.saveLoader.executeLoading(async () => {
         if (this.selectedFeatureToggle) {
            if (!this.selectedFeatureToggle.onlyForRegion) {
               this.selectedFeatureToggle.onlyForRegion = undefined
            }
            if (!this.selectedFeatureToggle.onlyForOrgsCreatedAfter) {
               this.selectedFeatureToggle.onlyForOrgsCreatedAfter = undefined
            }
            this.setupModal.close()
            setLoadingOnRow(this.featureToggles, this.selectedFeatureToggle, async () => {
               await apiClient.featureToggle.update.mutate(this.selectedFeatureToggle!)
            })
         }
      })
   }
}

export const { Provider: FeatureToggleStoreProvider, useProvidedStore: useFeatureToggleStore } = createStoreContext(FeatureToggleStore)
